import { PLAYER_COLORS } from "@/lib/api";
import Coin from "@/components/game/Coin";
import { STAT_META } from "@/components/game/CardStats";

const META = Object.fromEntries(STAT_META.map((m) => [m.key, m]));
const signed = (v) => (v > 0 ? `+${v}` : `${v}`);

function StatIcon({ k }) {
  if (k === "debt") return <Coin size={15} />;
  const m = META[k];
  return m ? <m.Icon s={15} c={m.color} /> : null;
}

function Line({ k, icon, label, detail, value, color }) {
  return (
    <div className="flex justify-between items-baseline gap-2 py-1" data-testid={`score-${k}-${color}`}>
      <dt className="text-[color:var(--ink-3)] inline-flex items-center gap-1.5 min-w-0">
        <span className="inline-flex shrink-0 self-center"><StatIcon k={icon} /></span>
        <span className="text-[color:var(--ink-2)]">{label}</span>
        {detail && (
          <span className="text-xs truncate" data-testid={`score-${k}-detail-${color}`}>{detail}</span>
        )}
      </dt>
      <dd className={`font-bold tabular-nums shrink-0 ${value < 0 ? "text-[color:var(--danger)]" : "text-[color:var(--ink)]"}`}>
        {signed(value ?? 0)}
      </dd>
    </div>
  );
}

const SUB = { fontSize: 12.5, color: "var(--ink-3)" };

function SubLine({ k, color, label, detail, value }) {
  return (
    <div className="flex justify-between items-baseline gap-2 py-0.5 pl-6" data-testid={`score-${k}-${color}`}>
      <dt style={SUB} className="min-w-0 truncate">
        {label}
        {detail && <span data-testid={`score-${k}-detail-${color}`}> · {detail}</span>}
      </dt>
      <dd className="tabular-nums shrink-0" style={{ ...SUB, color: value < 0 ? "var(--danger)" : "var(--ink-2)" }}>{signed(value ?? 0)}</dd>
    </div>
  );
}

/**
 * One player's score sheet (v3 resilience scoring), the same rows during play and at the end:
 *   Population · Resilience (green tiles, upgrades, network, grey buildings) · Debt · Total.
 * 12 rounds: population + resilience - debt. Ended at 3 degrees: resilience only (debt is shown, not subtracted).
 * `weights` comes from /scoring.
 */
export function ScoreSheet({ row, player, highlight, weights, final = false }) {
  const colorHex = PLAYER_COLORS[row.color] || "var(--ink-3)";
  const w = { population_weight: 1, resilience_weight: 1, debt_penalty: -1, grey_building_penalty: -1, ...(weights || {}) };
  const early = row.mode === "early";
  const rp = row.resilience_parts || {};
  const popBits = [row.population_coastal ? `coastal +${row.population_coastal}` : "", row.population_parks ? `parks +${row.population_parks}` : "", row.population_walls ? `-${row.population_walls} from sea walls` : ""].filter(Boolean);
  const mult = (n, wt) => (wt === 1 ? `${n}` : `${n} × ${wt}`);
  return (
    <li
      data-testid={`score-row-${row.color}`}
      className="border border-[color:var(--line)] bg-white p-4"
      style={{ boxShadow: highlight ? `6px 6px 0 0 ${colorHex}, 9px 9px 0 0 var(--ink)` : undefined }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="h-8 w-8 border border-[color:var(--line)]" style={{ background: colorHex }} />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-lg text-[color:var(--ink)] truncate">
            {player?.name || row.color}
          </div>
          {row.place && (
            <div className="text-xs text-[color:var(--ink-3)] uppercase tracking-wide">
              Place #{row.place}
              {row.shared_winner && " · shared winner"}
            </div>
          )}
        </div>
      </div>
      <dl className="text-sm divide-y divide-dashed divide-[var(--line)]">
        <div style={early ? { opacity: 0.6 } : undefined}>
          <Line k="population" icon="pop" color={row.color}
            label="Population"
            detail={popBits.length ? `(${popBits.join(", ")})` : ""}
            value={row.population_points ?? 0} />
        </div>
        <div>
          <Line k="resilience" icon="absorb" color={row.color}
            label="Resilience"
            detail={w.resilience_weight !== 1 ? `× ${w.resilience_weight}` : ""}
            value={row.resilience ?? 0} />
          <SubLine k="res-tiles" color={row.color} label="Green and blue tiles" value={rp.tiles ?? 0} />
          <SubLine k="res-upgrades" color={row.color} label="Green Roofs and Streets" value={rp.upgrades ?? 0} />
          <SubLine k="res-network" color={row.color} label="Network bonus" detail="+1 per green tile joined to another" value={rp.network ?? 0} />
          <SubLine k="res-grey" color={row.color} label="Grey buildings"
            detail={rp.grey_count ? `${rp.grey_count} × ${w.grey_building_penalty}` : "none"} value={rp.grey ?? 0} />
        </div>
        <div style={early ? { opacity: 0.6 } : undefined}>
          <Line k="debt" icon="debt" color={row.color}
            label="Debt"
            detail={early ? (row.debt ? `${row.debt}, not subtracted at 3°` : "") : (row.debt ? `${row.debt} × ${w.debt_penalty}` : "")}
            value={early ? 0 : (row.debt_penalty ?? 0)} />
        </div>
      </dl>
      <div
        className={`mt-2 pt-2 border-t border-[color:var(--line)] flex justify-between font-bold text-[color:var(--ink)] text-2xl`}
        data-testid={`score-total-${row.color}`}
      >
        <span>{final ? (early ? "Final resilience" : "Final score") : "Score if 12 rounds"}</span>
        <span className="tabular-nums">{row.total ?? 0}</span>
      </div>
      {!final && (
        <div className="flex justify-between text-xs text-[color:var(--ink-3)] mt-1" data-testid={`score-early-${row.color}`}>
          <span>If the game ends at 3° (resilience only)</span>
          <span className="tabular-nums font-bold">{row.total_early ?? row.resilience ?? 0}</span>
        </div>
      )}
    </li>
  );
}

// Tiebreak explanation, by ending - shared by the live and final views.
export function TiebreakLine({ ranking, players, order, mode = "full" }) {
  if (!ranking?.length) return null;
  const name = (c) => players?.find((p) => p.color === c)?.name || c;
  const repairs = ranking.map((r) => `${name(r.color)} ${r.flood_paid ?? 0}`).join(", ");
  const text = mode === "early"
    ? `Tie at 3°? Higher population wins, then fewest repairs paid (${repairs}). Still tied: shared win.`
    : `Tie after 12 rounds? Higher resilience wins, then fewest repairs paid (${repairs}). Still tied: shared win.`;
  return (
    <div data-testid="score-tiebreak"
      title="Repairs paid counts every repair charged to a player, including repairs that went into debt."
      className="mt-3 text-xs text-[color:var(--ink-3)]">
      {text}
    </div>
  );
}
